import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import Lottie from 'react-lottie';
import introLottie from '../assets/lottie/introLottie.json';
import nokiaLottieM from '../assets/lottie/nokia-graphic-mobile.json';

import pullQuote1 from '../assets/pullQuote1.png';
import pullQuote1M from '../assets/pullQuote1M.png';
import tempCloudImg from '../assets/tempCityImg.jpg';

const Body = () => {
  const { ref, inView } = useInView();
  const [width, setWidth] = useState(window.innerWidth);

  const breakpoints = {
    sm: 500,
    tab: 768,
    md: 1024,
    lg: 1280,
    xl: 1440
  };

  useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: introLottie,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  return (
    <div className="body">
      <div className="body__paragraph">
        <p>
          The sudden shift to remote working caught many organizations by surprise. If there’s an
          upside, it’s the digital innovation driving positive changes across industries as
          organizations breathe new life into existing IT environments. One result is the rise in
          hybrid cloud solutions that can bridge an organization’s entire IT environment—no matter
          how large or distributed it may be.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          It’s a paradigm shift that delivers innovations in apps and data while introducing
          security and management challenges. Yet the right hybrid cloud solution can actually help
          organizations improve their security posture, streamline the management of these complex
          environments, and allow them to innovate with confidence.
        </p>
      </div>
      <div className="body__paragraph">
        <h6>Innovation means building a bridge</h6>
        <p>
          “To meet the growing demands and evolving expectations of customers to remain competitive,
          every company will need to become a digital company,” explains Roanne Sones, Corporate
          Vice President of Azure Edge and Platform at Microsoft. “It’s already happening across
          every industry, with software- and data-powered disruptors pushing organizations of every
          size to accelerate their digital transformation.”
        </p>
      </div>

      <div ref={ref} className="body__lottie">
        {/* {inView ? ( */}
        <Lottie options={defaultOptions} className="lottie" />
        {/* ) : null} */}
        {/* <p className="caption">
          A hybrid cloud solution can share data, applications, and workloads across environments
          such as on-premises datacenter, an edge site, and even multiple cloud hosts
          (“multicloud”).
        </p> */}
      </div>
      <div className="body__paragraph">
        <p>
          For these organizations, hybrid cloud solutions offer a path forward with a mix of
          compute, storage, and services that work together across public and private environments.
          These may be located within an organization’s on-premises datacenter or edge sites, such
          as a factory, or even in multiple cloud hosts (“multicloud”). Data, applications, and
          workloads can be managed across these environments, enabling organizations to solve
          complex business problems on a massive scale and deliver innovative solutions faster.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          As organizations plan their digital transformation strategies, the real benefit of a
          hybrid cloud solution is agility, as Sones points out. But to get started, they “need a
          bridge that connects all the pieces of their operations,” she says. That bridge needs to
          span IT environments so an organization can manage the complexity of its digital
          landscapes more efficiently.
        </p>
      </div>
      <div className="body__paragraph">
        <h6>Innovation has no expiration date</h6>
        <p>
          Well-managed hybrid cloud platforms help even the most established enterprises modernize
          and compete alongside today’s digital disrupters. Consider telecommunications leader{' '}
          <a
            className="articleLink"
            href="https://sponsorcontent.cnn.com/interactive/microsoft/intel/hybridcloud/innovationandsecurity/"
            target="_blank"
            rel="noreferrer"
          >
            Nokia
          </a>
          . It’s been in business for more than 155 years but recently adopted a hybrid cloud
          solution to run an inventive AI ecosystem for its customers.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Or look to{' '}
          <a
            className="articleLink"
            href="https://sponsorcontent.cnn.com/interactive/microsoft/intel/hybridcloud/mountaintopview/"
            target="_blank"
            rel="noreferrer"
          >
            Ferguson
          </a>
          , the largest wholesale distributor of plumbing supplies in the United States. Going
          beyond physical pipelines to data pipelines, the company is using a hybrid cloud solution
          that connects the dots across a disparate IT landscape and effectively manages resources.
        </p>
      </div>

      <div className="body__paragraph">
        <p>
          Organizations like these reap the benefits of cloud adoption—greater scalability, reliable
          distributed services, and lower capital expenditures. Along the way, they learn how to
          address unique cloud challenges, such as the industry, regulatory, and geographical
          requirements governing the use and storage of data.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Telecommunications, manufacturing, and other highly regulated industries often require
          certain data and applications to be kept on-premises. Microsoft introduced Azure Arc as
          the answer to help manage data across these environments.
        </p>
      </div>
      <div className="body__pullQuote">
        <img src={width <= breakpoints.sm ? pullQuote1M : pullQuote1} alt="box with quote" />
      </div>
      <div className="body__paragraph">
        <p>
          Azure Arc is a set of technologies that unlocks new hybrid scenarios by bringing Azure
          services and management to any infrastructure. Today, customers are using it to organize,
          govern, and secure hundreds of millions of Azure resources daily. Azure Arc extends proven
          Azure management capabilities to on-premises datacenters, across cloud hosts, and at the
          edge. The result is a more consistent and unified approach for managing these different
          environments.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Organizations can also run Azure Arc–enabled data services anywhere and benefit from
          deployment in seconds instead of hours, virtually limitless scalability, and an
          always-current environment. For Nokia and Ferguson, that means less risk. Azure
          Arc–enabled servers stay up to date. For added security, organizations can use Microsoft’s
          security tools, like Microsoft Defender for Cloud with Azure Arc to protect non-Azure
          resources located on-premises or on other cloud providers. Customers can enjoy an
          environment that is evergreen, with no end of support and no resource-intensive
          migrations.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          “With Azure Arc, we’ve created a bridge between all those great innovations in Azure and
          organizations’ existing workloads and infrastructure, wherever they may be,” Sones
          explains. “Organizations can confidently invest in a single set of skills and processes
          that they can deploy wherever they’re needed.”
        </p>
      </div>
      <div className="body__paragraph">
        <h6>Innovation extends to smart hardware at the edge</h6>
        <p>
          In the era of the Internet of Things (IoT), Industry 4.0, and 5G networks, data is
          everywhere as innovations in hardware unleash an app revolution. Technology leaders like
          Microsoft and Intel are showing how software and hardware—the virtual and the
          physical—work in combination to unlock new opportunities while also helping you to protect
          your IT assets.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          “Microsoft and Intel collaborate to co-engineer solutions using our collective technology
          to address our customers’ key challenges around performance, security, and flexibility,”
          observes Rose Schooler, Intel Corporate Vice President Global Data Center Sales.
        </p>
      </div>

      <div className="body__photoBlock">
        <img src={tempCloudImg} alt="digital cloud" />
      </div>
      <div className="body__paragraph">
        <p>
          As they see it, innovation and security do not have to be mutually exclusive. Their
          customers want to move to the edge so they can capture and store more data, analyze it
          faster, and act on it sooner. And Microsoft and Intel are working together to make the
          process simpler.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Today, Intel advises enterprises that want to take advantage of the intelligent edge to
          get started by choosing the optimal hardware for networking, storing, and processing data.
          <sup>1</sup> The edge in performance is what the intelligent edge is all about. As an
          example, running AI and machine learning solutions at the source of the data—whether in a
          factory, at a branch office, or in the field—puts computing power where it’s most needed.
          The latest Intel innovation, 3rd Gen Intel® Xeon® Scalable processors, helps achieve this.
          Compared to earlier generations, they offer a significant boost in performance.
          <sup>2</sup>
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          That boost is why Microsoft offers edge computing solutions based on Intel. Those
          solutions are helping organizations provide the same consistent management, governance,
          and security for applications and data services on the edge that they do for on-premises
          and cloud environments.
        </p>
      </div>
      <div className="body__paragraph">
        <h6>Innovation without borders</h6>
        <p>
          Microsoft is unique in its hybrid cloud approach. Enterprises like Nokia and Ferguson can
          “bring the modernization, innovation, and transformation of the cloud to their
          organizations,” Sones points out, and can run wherever they need to run, however they need
          to run.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Options like Azure Arc–enabled data services and Azure Arc–enabled applications bring
          cloud efficiencies to edge computing and on-premises environments. Enabled services also
          provide Azure security benefits, like built-in policy enforcement and health monitoring.
          With Azure Arc, services that were once only available in Azure can now be extended
          virtually anywhere.
        </p>
      </div>
      <div className="body__paragraph">
        <p>Clearly, innovation and security do go hand in hand.</p>
      </div>
      <a
        href="https://azure.microsoft.com/en-us/services/azure-arc/hybrid-data-services/#overview"
        target="_blank"
        rel="noreferrer"
      >
        <div className="body__cta">
          <div className="ctaText">
            <p> Azure Arc–Enabled Data Services & Management | Microsoft Azure</p>
          </div>
          <div className="ctaButton">
            <button>Learn More</button>
          </div>
        </div>
      </a>
    </div>
  );
};

export default Body;
